import { HierarcicalObjectReference, ObjectData, ObjectId, SearchPattern } from "@novorender/data-js-api";

import { AppDispatch } from "app/store";
import { Tree } from "components/objectTree/types";
import { SelectedProperty } from "components/window/core/properties/hooks/usePropertySelection";
import { hiddenActions } from "contexts/hidden";
import { DispatchHidden } from "contexts/hidden/reducer";
import { HighlightCollection, highlightCollectionsActions } from "contexts/highlightCollections";
import { DispatchHighlightCollection } from "contexts/highlightCollections/reducer";
import { highlightActions } from "contexts/highlighted";
import { DispatchHighlighted } from "contexts/highlighted/reducer";
import { selectionBasketActions } from "contexts/selectionBasket";
import { DispatchSelectionBasket } from "contexts/selectionBasket/reducer";
import { ModelTreeNodeData } from "features/modelTree/types";
import { renderActions } from "features/render";
import { ObjectVisibility } from "features/render/types";
import { undoRedoActions } from "features/undoRedo";
import { telemetry } from "telemetry";
import { NodeType } from "types/misc";

import { AppObjectDB } from "./appObjectDb";
import { extractObjectIds, getFilePathFromObjectPath } from "./objectData";
import { getDescendants, searchByParentPath, searchDeepByPatterns } from "./search";
import { collectDescendantIds } from "./treeHelpers";

export interface SearchCriteria {
    property: string;
    value: string[];
    exact?: boolean;
}

export async function findSimilarObjects({
    db,
    searchCriteria,
}: {
    db: AppObjectDB;
    searchCriteria: SearchCriteria[];
}): Promise<number[]> {
    const allIds: number[] = [];

    // Convert our search criteria to SearchPattern format
    const searchPatterns: SearchPattern[] = searchCriteria.map((criteria) => ({
        property: criteria.property,
        value: criteria.value,
        exact: criteria.exact ?? true,
    }));

    await searchDeepByPatterns({
        db,
        searchPatterns,
        callback: (ids) => {
            allIds.push(...ids);
        },
    });

    return allIds;
}

/**
 * Get default search criteria for an object based on its file type and properties.
 * This replicates the original logic from procoreStamp but returns the criteria
 * instead of directly searching.
 */
export function getDefaultSearchCriteria(obj: ObjectData): SearchCriteria[] {
    if (!obj) {
        console.warn("getDefaultSearchCriteria called with null/undefined object");
        return [];
    }

    if (!obj.path || !obj.name) {
        console.warn("Object missing required path or name:", { path: obj.path, name: obj.name });
        return [];
    }

    const filePath = getFilePathFromObjectPath(obj.path)?.toLowerCase();

    if (filePath) {
        if (filePath.endsWith(".ifc")) {
            const ifcClass = obj.properties?.find((p) => p[0].toLowerCase() === "ifcclass")?.[1];
            if (ifcClass) {
                return [{ property: "ifcClass", value: [ifcClass], exact: true }];
            }
        } else if (filePath.endsWith(".dwg")) {
            const layer = obj.properties?.find((p) => p[0].toLowerCase() === "dwg/layer")?.[1];
            if (layer) {
                return [{ property: "dwg/layer", value: [layer], exact: true }];
            }
        }
    }

    // Fallback to name
    return [{ property: "name", value: [obj.name], exact: true }];
}

export function hideObjects(
    objectIds: number[],
    {
        dispatch,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        dispatchHighlightCollections,
        currentSecondaryHighlightIds,
        currentMainObject,
        currentHiddenIds,
        currentLastHiddenIds,
        preserveSelection = false,
    }: {
        dispatch: AppDispatch;
        dispatchHighlighted: DispatchHighlighted;
        dispatchHidden: DispatchHidden;
        dispatchSelectionBasket: DispatchSelectionBasket;
        dispatchHighlightCollections?: DispatchHighlightCollection; // Not used in Procore
        currentSecondaryHighlightIds?: number[];
        currentMainObject?: number;
        currentHiddenIds: Record<ObjectId, true | undefined>;
        currentLastHiddenIds: number[];
        preserveSelection?: boolean;
    },
): void {
    // Most of the time highlights include main object but not always - add it here explicitly
    const objectIdsWithMain =
        currentMainObject && !objectIds.includes(currentMainObject) ? [...objectIds, currentMainObject] : objectIds;
    const newlyHiddenIds = objectIdsWithMain.filter((id) => !currentHiddenIds[id]);

    if (newlyHiddenIds.length === 0) {
        return;
    }

    const hide = () => {
        if (!preserveSelection) {
            dispatch(renderActions.setMainObject(undefined));
            dispatchHighlighted(highlightActions.remove(objectIdsWithMain));
            dispatchSelectionBasket(selectionBasketActions.remove(objectIdsWithMain));
            dispatchHighlightCollections?.(
                highlightCollectionsActions.setIds(HighlightCollection.SecondaryHighlight, []),
            );
        }

        dispatchHidden(hiddenActions.add(newlyHiddenIds));
        dispatchHidden(hiddenActions.setLastHiddenIds(newlyHiddenIds));
    };
    hide();

    dispatch(
        undoRedoActions.push({
            description: `Hide ${newlyHiddenIds.length} object(s)`,
            undoCallback: () => {
                if (!preserveSelection) {
                    dispatch(renderActions.setMainObject(currentMainObject));
                    dispatchHighlighted(highlightActions.add(objectIdsWithMain));
                    dispatchSelectionBasket(selectionBasketActions.add(objectIdsWithMain));
                    if (dispatchHighlightCollections && currentSecondaryHighlightIds) {
                        dispatchHighlightCollections(
                            highlightCollectionsActions.setIds(
                                HighlightCollection.SecondaryHighlight,
                                currentSecondaryHighlightIds,
                            ),
                        );
                    }
                }
                if (newlyHiddenIds.length > 0) {
                    dispatchHidden(hiddenActions.remove(newlyHiddenIds));
                    dispatchHidden(hiddenActions.setLastHiddenIds(currentLastHiddenIds));
                }
            },
            redoCallback: hide,
        }),
    );
}

export function showObjects(
    objectIds: number[],
    {
        dispatch,
        dispatchHidden,
    }: {
        dispatch: AppDispatch;
        dispatchHidden: DispatchHidden;
    },
): void {
    const show = () => {
        dispatchHidden(hiddenActions.remove(objectIds));
    };
    show();

    dispatch(
        undoRedoActions.push({
            description: `Show ${objectIds.length} object(s)`,
            undoCallback: () => {
                dispatchHidden(hiddenActions.add(objectIds));
            },
            redoCallback: show,
        }),
    );
}

export async function hideSimilarObjects(
    obj: ObjectData,
    {
        db,
        dispatch,
        dispatchHighlighted,
        dispatchHidden,
        dispatchSelectionBasket,
        currentHiddenIds,
        currentMainObject,
        currentHighlightedIds,
        currentSelectionBasketIds,
        currentLastHiddenIds,
    }: {
        db: AppObjectDB;
        dispatch: AppDispatch;
        dispatchHighlighted: DispatchHighlighted;
        dispatchHidden: DispatchHidden;
        dispatchSelectionBasket: DispatchSelectionBasket;
        currentHiddenIds: number[];
        currentMainObject?: number;
        currentHighlightedIds: number[];
        currentSelectionBasketIds: number[];
        currentLastHiddenIds: number[];
    },
    searchCriteria?: SearchCriteria[],
): Promise<void> {
    const criteria = searchCriteria ?? getDefaultSearchCriteria(obj);

    if (criteria.length === 0) {
        console.warn("No valid search criteria for hideSimilarObjects");
        return;
    }

    const objectIds = await findSimilarObjects({ db, searchCriteria: criteria });

    const execute = () => {
        dispatch(renderActions.setMainObject(undefined));
        dispatchHighlighted(highlightActions.remove(objectIds));
        dispatchHidden(hiddenActions.add(objectIds));
        dispatchSelectionBasket(selectionBasketActions.remove(objectIds));
        dispatchHidden(hiddenActions.setLastHiddenIds(objectIds));
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: `Hide ${objectIds.length} similar object(s)`,
            undoCallback: () => {
                dispatch(renderActions.setMainObject(currentMainObject));
                dispatchHighlighted(highlightActions.setIds(currentHighlightedIds));
                dispatchHidden(hiddenActions.setIds(currentHiddenIds));
                dispatchSelectionBasket(selectionBasketActions.set(currentSelectionBasketIds));
                dispatchHidden(hiddenActions.setLastHiddenIds(currentLastHiddenIds));
            },
            redoCallback: execute,
        }),
    );
}

export function unhideLastHiddenObjects(
    lastHiddenIds: number[],
    {
        dispatch,
        dispatchHidden,
    }: {
        dispatch: AppDispatch;
        dispatchHidden: DispatchHidden;
    },
): void {
    if (lastHiddenIds.length === 0) {
        return;
    }

    const unhide = () => {
        dispatchHidden(hiddenActions.remove(lastHiddenIds));
        dispatchHidden(hiddenActions.setLastHiddenIds([]));
    };
    unhide();

    dispatch(
        undoRedoActions.push({
            description: `Unhide last hidden objects`,
            undoCallback: () => {
                dispatchHidden(hiddenActions.add(lastHiddenIds));
                dispatchHidden(hiddenActions.setLastHiddenIds(lastHiddenIds));
            },
            redoCallback: unhide,
        }),
    );
}

export function unselectAll({
    dispatch,
    dispatchHighlighted,
    currentHighlightedIds,
    currentMainObject,
}: {
    dispatch: AppDispatch;
    dispatchHighlighted: DispatchHighlighted;
    currentHighlightedIds: number[];
    currentMainObject: number | undefined;
}): void {
    const unselect = () => {
        dispatchHighlighted(highlightActions.setIds([]));
        dispatch(renderActions.setMainObject(undefined));
    };
    unselect();

    dispatch(
        undoRedoActions.push({
            description: "Unselect objects",
            undoCallback: () => {
                dispatchHighlighted(highlightActions.setIds(currentHighlightedIds));
                dispatch(renderActions.setMainObject(currentMainObject));
            },
            redoCallback: unselect,
        }),
    );
}

function selectObjectsRaw(
    objectIds: number[],
    { dispatchHighlighted }: { dispatchHighlighted: DispatchHighlighted },
): void {
    dispatchHighlighted(highlightActions.setIds(objectIds));
}

export function selectObjects(
    objectIds: number[],
    {
        dispatch,
        dispatchHighlighted,
        mainObject,
        currentHighlightedIds,
        currentMainObject,
    }: {
        dispatch: AppDispatch;
        dispatchHighlighted: DispatchHighlighted;
        mainObject: number | undefined;
        currentHighlightedIds: number[];
        currentMainObject: number | undefined;
    },
): void {
    const select = () => {
        dispatchHighlighted(highlightActions.setIds(objectIds));
        dispatch(renderActions.setMainObject(mainObject));
    };
    select();

    dispatch(
        undoRedoActions.push({
            description: `Select ${objectIds.length} object(s)`,
            undoCallback: () => {
                dispatchHighlighted(highlightActions.setIds(currentHighlightedIds));
                dispatch(renderActions.setMainObject(currentMainObject));
            },
            redoCallback: select,
        }),
    );
}

export async function selectSimilarObjects(
    obj: ObjectData,
    {
        db,
        dispatch,
        dispatchHighlighted,
        currentHighlightedIds,
    }: {
        db: AppObjectDB;
        dispatch: AppDispatch;
        dispatchHighlighted: DispatchHighlighted;
        currentHighlightedIds: number[];
    },
    searchCriteria?: SearchCriteria[],
): Promise<void> {
    const criteria = searchCriteria ?? getDefaultSearchCriteria(obj);

    if (criteria.length === 0) {
        console.warn("No valid search criteria for selectSimilarObjects");
        return;
    }

    const similarIds = await findSimilarObjects({ db, searchCriteria: criteria });

    const execute = () => {
        selectObjectsRaw(similarIds, { dispatchHighlighted });
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: `Select ${similarIds.length} similar object(s)`,
            undoCallback: () => {
                selectObjectsRaw(currentHighlightedIds, { dispatchHighlighted });
            },
            redoCallback: execute,
        }),
    );
}

export function isolateObjects({
    dispatch,
    currentDefaultVisibility,
}: {
    dispatch: AppDispatch;
    currentDefaultVisibility: ObjectVisibility;
}): void {
    const execute = () => {
        dispatch(renderActions.setPreIsolationVisibility(currentDefaultVisibility));
        dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Transparent));
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: "Isolate objects",
            undoCallback: () => {
                dispatch(renderActions.clearPreIsolationVisibility());
                dispatch(renderActions.setDefaultVisibility(currentDefaultVisibility));
            },
            redoCallback: execute,
        }),
    );
}

export function unisolateObjects({
    dispatch,
    restoreToVisibility,
}: {
    dispatch: AppDispatch;
    /** The visibility to restore to — typically the value saved as preIsolationVisibility. */
    restoreToVisibility: ObjectVisibility;
}): void {
    const execute = () => {
        dispatch(renderActions.clearPreIsolationVisibility());
        dispatch(renderActions.setDefaultVisibility(restoreToVisibility));
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: "Unisolate objects",
            undoCallback: () => {
                dispatch(renderActions.setPreIsolationVisibility(restoreToVisibility));
                dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Transparent));
            },
            redoCallback: execute,
        }),
    );
}

export function isolateObjectsInXRayMode({
    dispatch,
    currentDefaultVisibility,
}: {
    dispatch: AppDispatch;
    currentDefaultVisibility: ObjectVisibility;
}): void {
    const execute = () => {
        dispatch(renderActions.setPreIsolationVisibility(currentDefaultVisibility));
        dispatch(renderActions.setVisibilityMode(ObjectVisibility.Xray));
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: "Isolate objects in X-ray mode",
            undoCallback: () => {
                dispatch(renderActions.clearPreIsolationVisibility());
                dispatch(renderActions.setVisibilityMode(currentDefaultVisibility));
            },
            redoCallback: execute,
        }),
    );
}

export function unisolateObjectsFromXRayMode({
    dispatch,
    restoreToVisibility,
}: {
    dispatch: AppDispatch;
    /** The visibility to restore to — typically the value saved as preIsolationVisibility. */
    restoreToVisibility: ObjectVisibility;
}): void {
    const execute = () => {
        dispatch(renderActions.clearPreIsolationVisibility());
        dispatch(renderActions.setVisibilityMode(restoreToVisibility));
    };
    execute();

    dispatch(
        undoRedoActions.push({
            description: "Unisolate objects from X-ray mode",
            undoCallback: () => {
                dispatch(renderActions.setPreIsolationVisibility(restoreToVisibility));
                dispatch(renderActions.setVisibilityMode(ObjectVisibility.Xray));
            },
            redoCallback: execute,
        }),
    );
}

export function exitXRayMode({ dispatch }: { dispatch: AppDispatch }): void {
    dispatch(renderActions.setVisibilityMode(ObjectVisibility.Neutral));
}

export function addLoadingHandle({ dispatch }: { dispatch: AppDispatch }): number {
    const handle = performance.now();
    dispatch(renderActions.addLoadingHandle(handle));
    return handle;
}

export function removeLoadingHandle(handle: number, { dispatch }: { dispatch: AppDispatch }): void {
    dispatch(renderActions.removeLoadingHandle(handle));
}

export async function withLoadingState<T>(
    operation: () => Promise<T>,
    { dispatch }: { dispatch: AppDispatch },
): Promise<T> {
    const handle = addLoadingHandle({ dispatch });
    try {
        return await operation();
    } finally {
        removeLoadingHandle(handle, { dispatch });
    }
}

export async function isolateSimilarInXray(
    selectedProperties: Map<string, SelectedProperty>,
    {
        db,
        dispatch,
        dispatchHighlighted,
        currentDefaultVisibility,
    }: {
        db: AppObjectDB;
        dispatch: AppDispatch;
        dispatchHighlighted: DispatchHighlighted;
        currentDefaultVisibility: ObjectVisibility;
    },
): Promise<void> {
    // If no properties selected, clear and exit X-ray mode
    if (selectedProperties.size === 0) {
        selectObjectsRaw([], { dispatchHighlighted });
        exitXRayMode({ dispatch });
        return;
    }

    // Convert selected properties to search criteria
    const searchCriteria: SearchCriteria[] = [];
    for (const selectedProperty of selectedProperties.values()) {
        searchCriteria.push({
            property: selectedProperty.searchPropertyName,
            value: [selectedProperty.propertyValue],
            exact: true,
        });
    }

    try {
        await withLoadingState(
            async () => {
                const similarIds = await findSimilarObjects({ db, searchCriteria });
                selectObjectsRaw(similarIds, { dispatchHighlighted });
                isolateObjectsInXRayMode({
                    dispatch,
                    currentDefaultVisibility,
                });
            },
            { dispatch },
        );
    } catch (error) {
        console.error("Error in isolateSimilarInXray:", error);
        telemetry.trackCaughtException({
            source: "utils.objectActions.isolateSimilarInXray",
            category: "data",
            error,
            severity: "error",
        });
    }
}

/**
 * Select objects in a normal tree (non-filtered).
 * For folders, selects all descendants by querying the database.
 * Includes the folder ID itself in the selection set.
 *
 * @param dispatchHidden - Optional. When provided, selected objects will be automatically
 *                         unhidden. In the Object Tree window, this is intentionally NOT passed
 *                         to allow objects to exist in both selected and hidden sets simultaneously.
 *                         Other components (like Model Tree) may pass this to maintain automatic
 *                         unhiding behavior.
 */
export async function selectObjectHierarchy({
    nodes,
    db,
    abortSignal,
    setLoading,
    dispatchHighlighted,
    dispatchHidden,
}: {
    nodes: HierarcicalObjectReference[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHighlighted: DispatchHighlighted;
    dispatchHidden?: DispatchHidden;
}): Promise<number[]> {
    const isLeaf = nodes[0].type === NodeType.Leaf;
    if (isLeaf) {
        const ids = nodes.map((n) => n.id);
        dispatchHighlighted(highlightActions.add(ids));
        if (dispatchHidden) {
            dispatchHidden(hiddenActions.remove(ids));
        }
        return ids;
    }

    setLoading(true);
    const node = nodes[0];
    let addedIds: number[] = [];
    try {
        try {
            addedIds = await getDescendants({ db, parentNode: node, abortSignal });
            dispatchHighlighted(highlightActions.add(addedIds));
            if (dispatchHidden) {
                dispatchHidden(hiddenActions.remove(addedIds));
            }
        } catch {
            await searchByParentPath({
                db,
                abortSignal,
                parentPath: node.path,
                callback: (refs) => {
                    addedIds = extractObjectIds(refs);
                    dispatchHighlighted(highlightActions.add(addedIds));
                    if (dispatchHidden) {
                        dispatchHidden(hiddenActions.remove(addedIds));
                    }
                },
            });
        }

        if (!abortSignal.aborted) {
            dispatchHighlighted(highlightActions.add([node.id]));
            if (dispatchHidden) {
                dispatchHidden(hiddenActions.remove([node.id]));
            }
            addedIds = [...addedIds, node.id];
            setLoading(false);
        }
        return addedIds;
    } catch (e) {
        if (!abortSignal.aborted) {
            setLoading(false);
            telemetry.trackCaughtException({
                source: "utils.objectActions.selectObjectHierarchy",
                category: "data",
                error: e,
            });
        }
        return addedIds;
    }
}

export async function unselectObjectHierarchy({
    nodes,
    db,
    abortSignal,
    setLoading,
    dispatchHighlighted,
}: {
    nodes: HierarcicalObjectReference[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHighlighted: DispatchHighlighted;
}): Promise<number[]> {
    const isLeaf = nodes[0].type === NodeType.Leaf;
    const ids = nodes.map((n) => n.id);
    if (isLeaf) {
        dispatchHighlighted(highlightActions.remove(ids));
        return ids;
    }

    dispatchHighlighted(highlightActions.remove(ids));
    let removedIds = [...ids];
    setLoading(true);

    const node = nodes[0];
    try {
        try {
            const descendantIds = await getDescendants({ db, parentNode: node, abortSignal });
            removedIds = [...removedIds, ...descendantIds];
            dispatchHighlighted(highlightActions.remove(descendantIds));
        } catch {
            await searchByParentPath({
                db,
                abortSignal,
                parentPath: node.path,
                callback: (refs) => {
                    const extracted = extractObjectIds(refs);
                    removedIds = [...removedIds, ...extracted];
                    dispatchHighlighted(highlightActions.remove(extracted));
                },
            });
        }
    } catch (e) {
        if (!abortSignal.aborted) {
            telemetry.trackCaughtException({
                source: "utils.objectActions.unselectObjectHierarchy",
                category: "data",
                error: e,
            });
        }
    } finally {
        if (!abortSignal.aborted) {
            setLoading(false);
        }
    }
    return removedIds;
}

/**
 * Select objects from a filtered tree (e.g., search results).
 * Only selects objects that exist in the filteredObjectIds set AND are descendants of the clicked node.
 * Note: Synchronous operation - no loading state needed since we traverse the in-memory tree.
 *
 * @param dispatchHidden - Optional. When provided, selected objects will be automatically
 *                         unhidden. In the Object Tree window, this is intentionally NOT passed
 *                         to allow objects to exist in both selected and hidden sets simultaneously.
 *                         Other components may pass this to maintain automatic unhiding behavior.
 */
export async function selectObjectHierarchyFromFilteredTree({
    nodes,
    filteredObjectIds,
    dispatchHighlighted,
    dispatchHidden,
    tree,
}: {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dispatchHighlighted: DispatchHighlighted;
    dispatchHidden?: DispatchHidden;
    tree: Tree<ModelTreeNodeData>;
}): Promise<number[]> {
    const node = nodes[0];
    const isLeaf = node.type === NodeType.Leaf;

    // If it's a leaf, just select it directly
    if (isLeaf) {
        const allIds = nodes.map((n) => n.id);
        const ids = allIds.filter((id) => filteredObjectIds.has(id));
        dispatchHighlighted(highlightActions.add(ids));
        if (dispatchHidden) {
            dispatchHidden(hiddenActions.remove(ids));
        }
        return ids;
    }

    // For folders in a filtered tree: Use the tree structure to find which
    // filtered IDs are descendants of this node
    const treeNode = tree[node.path];
    if (!treeNode) {
        // This should never happen - it means our tree state is inconsistent
        console.error(`Node not found in filtered tree: ${node.path}. Selecting only clicked node(s).`);
        // At least only select what the user actually clicked
        const idsToSelect = nodes.map((n) => n.id).filter((id) => filteredObjectIds.has(id));
        dispatchHighlighted(highlightActions.add(idsToSelect));
        if (dispatchHidden) {
            dispatchHidden(hiddenActions.remove(idsToSelect));
        }
        return idsToSelect;
    }

    // Collect descendant IDs from the tree that are in the filtered set
    const descendantIds = collectDescendantIds(treeNode, tree, filteredObjectIds);

    // Also include the parent node itself if it's in the filtered set
    const idsToSelect = [...descendantIds];
    if (filteredObjectIds.has(node.id)) {
        idsToSelect.push(node.id);
    }

    dispatchHighlighted(highlightActions.add(idsToSelect));
    if (dispatchHidden) {
        dispatchHidden(hiddenActions.remove(idsToSelect));
    }
    return idsToSelect;
}

/**
 * Unselect objects from a filtered tree (e.g., search results).
 * Only unselects objects that exist in the filteredObjectIds set AND are descendants of the clicked node.
 * Note: Synchronous operation - no loading state needed since we traverse the in-memory tree.
 */
export async function unselectObjectHierarchyFromFilteredTree({
    nodes,
    filteredObjectIds,
    dispatchHighlighted,
    tree,
}: {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dispatchHighlighted: DispatchHighlighted;
    tree: Tree<ModelTreeNodeData>;
}): Promise<number[]> {
    const node = nodes[0];
    const isLeaf = node.type === NodeType.Leaf;
    const ids = nodes.map((n) => n.id);

    // If it's a leaf, just unselect it directly (filtered to only those in filtered set)
    if (isLeaf) {
        const idsToRemove = ids.filter((id) => filteredObjectIds.has(id));
        dispatchHighlighted(highlightActions.remove(idsToRemove));
        return idsToRemove;
    }

    // For folders in a filtered tree: Use the tree structure to find which
    // filtered IDs are descendants of this node
    const treeNode = tree[node.path];
    if (!treeNode) {
        // This should never happen - it means our tree state is inconsistent
        console.error(`Node not found in filtered tree: ${node.path}. Unselecting only clicked node(s).`);
        // At least only unselect what the user actually clicked
        const idsToUnselect = ids.filter((id) => filteredObjectIds.has(id));
        dispatchHighlighted(highlightActions.remove(idsToUnselect));
        return idsToUnselect;
    }

    // Collect descendant IDs from the tree that are in the filtered set
    const descendantIds = collectDescendantIds(treeNode, tree, filteredObjectIds);

    // Also include the parent node itself if it's in the filtered set
    const idsToUnselect = [...descendantIds];
    if (filteredObjectIds.has(node.id)) {
        idsToUnselect.push(node.id);
    }

    dispatchHighlighted(highlightActions.remove(idsToUnselect));
    return idsToUnselect;
}

/**
 * Hide objects in a normal tree.
 * For folders, hides all descendants by querying the database.
 * Includes the folder ID itself in the hidden set.
 */
export async function hideObjectHierarchy({
    nodes,
    db,
    abortSignal,
    setLoading,
    dispatchHidden,
}: {
    nodes: HierarcicalObjectReference[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHidden: DispatchHidden;
}): Promise<number[]> {
    const isLeaf = nodes[0].type === NodeType.Leaf;
    if (isLeaf) {
        const ids = nodes.map((n) => n.id);
        dispatchHidden(hiddenActions.add(ids));
        dispatchHidden(hiddenActions.setLastHiddenIds(ids));
        return ids;
    }

    setLoading(true);
    const node = nodes[0];
    let addedIds: number[] = [];
    try {
        try {
            addedIds = await getDescendants({ db, parentNode: node, abortSignal });
            dispatchHidden(hiddenActions.add(addedIds));
            dispatchHidden(hiddenActions.setLastHiddenIds(addedIds));
        } catch {
            await searchByParentPath({
                db,
                abortSignal,
                parentPath: node.path,
                callback: (refs) => {
                    addedIds = extractObjectIds(refs);
                    dispatchHidden(hiddenActions.add(addedIds));
                    dispatchHidden(hiddenActions.setLastHiddenIds(addedIds));
                },
            });
        }

        if (!abortSignal.aborted) {
            dispatchHidden(hiddenActions.add([node.id]));
            addedIds = [...addedIds, node.id];
            dispatchHidden(hiddenActions.setLastHiddenIds(addedIds));
            setLoading(false);
        }
    } catch (e) {
        if (!abortSignal.aborted) {
            setLoading(false);
            telemetry.trackCaughtException({
                source: "utils.objectActions.hideObjectHierarchy",
                category: "data",
                error: e,
            });
        }
    }
    return addedIds;
}

/**
 * Show (unhide) objects in a normal tree.
 * For folders, shows all descendants by querying the database.
 * Removes both the folder ID and all descendant IDs from the hidden set.
 */
export async function showObjectHierarchy({
    nodes,
    db,
    abortSignal,
    setLoading,
    dispatchHidden,
}: {
    nodes: HierarcicalObjectReference[];
    db: AppObjectDB;
    abortSignal: AbortSignal;
    setLoading: (value: boolean) => void;
    dispatchHidden: DispatchHidden;
}): Promise<number[]> {
    const isLeaf = nodes[0].type === NodeType.Leaf;
    const ids = nodes.map((n) => n.id);
    if (isLeaf) {
        dispatchHidden(hiddenActions.remove(ids));
        return ids;
    }

    dispatchHidden(hiddenActions.remove(ids));
    let removedIds = [...ids];
    setLoading(true);

    const node = nodes[0];
    try {
        try {
            const descendantIds = await getDescendants({ db, parentNode: node, abortSignal });
            removedIds = [...removedIds, ...descendantIds];
            dispatchHidden(hiddenActions.remove(descendantIds));
        } catch {
            await searchByParentPath({
                db,
                abortSignal,
                parentPath: node.path,
                callback: (refs) => {
                    const extracted = extractObjectIds(refs);
                    removedIds = [...removedIds, ...extracted];
                    dispatchHidden(hiddenActions.remove(extracted));
                },
            });
        }
    } catch (e) {
        if (!abortSignal.aborted) {
            telemetry.trackCaughtException({
                source: "utils.objectActions.showObjectHierarchy",
                category: "data",
                error: e,
            });
        }
    } finally {
        if (!abortSignal.aborted) {
            setLoading(false);
        }
    }
    return removedIds;
}

/**
 * Hide objects from a filtered tree (e.g., search results).
 * Only hides objects that exist in the filteredObjectIds set AND are descendants of the clicked node.
 * Note: Synchronous operation - no loading state needed since we traverse the in-memory tree.
 */
export async function hideObjectHierarchyFromFilteredTree({
    nodes,
    filteredObjectIds,
    dispatchHidden,
    tree,
}: {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dispatchHidden: DispatchHidden;
    tree: Tree<ModelTreeNodeData>;
}): Promise<number[]> {
    const node = nodes[0];
    const isLeaf = node.type === NodeType.Leaf;

    // If it's a leaf, just hide it directly
    if (isLeaf) {
        const allIds = nodes.map((n) => n.id);
        const ids = allIds.filter((id) => filteredObjectIds.has(id));
        dispatchHidden(hiddenActions.add(ids));
        dispatchHidden(hiddenActions.setLastHiddenIds(ids));
        return ids;
    }

    // For folders in a filtered tree: Use the tree structure to find which
    // filtered IDs are descendants of this node
    const treeNode = tree[node.path];
    if (!treeNode) {
        // This should never happen - it means our tree state is inconsistent
        console.error(`Node not found in filtered tree: ${node.path}. Hiding only clicked node(s).`);
        // At least only hide what the user actually clicked
        const idsToHide = nodes.map((n) => n.id).filter((id) => filteredObjectIds.has(id));
        dispatchHidden(hiddenActions.add(idsToHide));
        dispatchHidden(hiddenActions.setLastHiddenIds(idsToHide));
        return idsToHide;
    }

    // Collect descendant IDs from the tree that are in the filtered set
    const descendantIds = collectDescendantIds(treeNode, tree, filteredObjectIds);

    // Also include the parent node itself if it's in the filtered set
    const idsToHide = [...descendantIds];
    if (filteredObjectIds.has(node.id)) {
        idsToHide.push(node.id);
    }

    dispatchHidden(hiddenActions.add(idsToHide));
    dispatchHidden(hiddenActions.setLastHiddenIds(idsToHide));
    return idsToHide;
}

/**
 * Show (unhide) objects from a filtered tree (e.g., search results).
 * Only unhides objects that exist in the filteredObjectIds set AND are descendants of the clicked node.
 * Note: Synchronous operation - no loading state needed since we traverse the in-memory tree.
 */
export async function showObjectHierarchyFromFilteredTree({
    nodes,
    filteredObjectIds,
    dispatchHidden,
    tree,
}: {
    nodes: HierarcicalObjectReference[];
    filteredObjectIds: Set<number>;
    dispatchHidden: DispatchHidden;
    tree: Tree<ModelTreeNodeData>;
}): Promise<number[]> {
    const node = nodes[0];
    const isLeaf = node.type === NodeType.Leaf;
    const ids = nodes.map((n) => n.id);

    if (isLeaf) {
        const filteredIds = ids.filter((id) => filteredObjectIds.has(id));
        dispatchHidden(hiddenActions.remove(filteredIds));
        return filteredIds;
    }

    // For folders: find all descendants in the filtered tree and unhide them
    const treeNode = tree[node.path];
    if (!treeNode) {
        console.error(`Node not found in filtered tree: ${node.path}. Showing only clicked node(s).`);
        const idsToShow = ids.filter((id) => filteredObjectIds.has(id));
        dispatchHidden(hiddenActions.remove(idsToShow));
        return idsToShow;
    }

    // Collect descendant IDs from the tree that are in the filtered set
    const descendantIds = collectDescendantIds(treeNode, tree, filteredObjectIds);

    // Also include the parent node itself if it's in the filtered set
    const idsToShow = [...descendantIds];
    if (filteredObjectIds.has(node.id)) {
        idsToShow.push(node.id);
    }

    dispatchHidden(hiddenActions.remove(idsToShow));
    return idsToShow;
}
